import { Helmet } from "react-helmet-async";
import { SITE_ORIGIN, canonicalUrl } from "@/lib/site-url";

// Head metadata for a public marketing page: title + description (what the
// nine views already set by hand), plus the canonical URL and the Open
// Graph / Twitter pairs they were missing.
//
// Only the public `MarketingShell` routes render this. The auth-gated app is
// disallowed in /robots.txt and has no indexable content, so it stays on the
// index.html fallback title.
//
// The social card is a single site-wide image rather than per-page art:
// public/og-image.png, generated from the brand logo by
// scripts/generate-images.mjs. Pass `image` to override it for a page that
// earns its own.
type SeoProps = {
  title: string;
  description: string;
  /** Route path, e.g. "/functii". Made absolute against the canonical origin. */
  path: string;
  /** Help-centre articles are "article"; everything else is a "website". */
  type?: "website" | "article";
  /** Absolute URL of a page-specific social image. Defaults to the site card. */
  image?: string;
};

const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-image.png`;

export function Seo({
  title,
  description,
  path,
  type = "website",
  image = DEFAULT_OG_IMAGE,
}: SeoProps) {
  const url = canonicalUrl(path);
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Asistent Auto" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
}
